import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apiFetch, ApiClientError } from "@/lib/api-client";
import type { InboxMessage } from "./use-inbox-conversation";

export interface SendInboxMessageInput {
  brandId: string;
  conversationId: string;
  text?: string;
  attachmentUrl?: string;
  attachmentType?: "image" | "video" | "audio" | "file";
}

/**
 * Sends a DM via the outbox pattern. The server inserts a PENDING row,
 * calls Zernio, then returns the row as either SENT (200) or FAILED (202).
 *
 * The hook optimistically appends a PENDING bubble to the conversation
 * messages cache, then replaces it with the server response.
 */
export function useSendInboxMessage() {
  const qc = useQueryClient();
  return useMutation<
    InboxMessage,
    ApiClientError,
    SendInboxMessageInput,
    { optimisticId: string } | undefined
  >({
    mutationFn: async (input) => {
      // The API can return 202 with a FAILED row + error envelope.
      // apiFetch throws on non-2xx; 202 is OK. We always read .data.
      return apiFetch<InboxMessage>(
        `/brands/${input.brandId}/inbox/conversations/${input.conversationId}/messages`,
        {
          method: "POST",
          body: JSON.stringify({
            text: input.text,
            attachmentUrl: input.attachmentUrl,
            attachmentType: input.attachmentType,
          }),
        },
      );
    },
    onMutate: async (input) => {
      await qc.cancelQueries({
        queryKey: ["inbox-conversation", input.brandId, input.conversationId],
      });
      const optimisticId = `optimistic-${Date.now()}`;
      const optimistic: InboxMessage = {
        id: optimisticId,
        conversationId: input.conversationId,
        zernioMsgId: optimisticId,
        direction: "out",
        senderId: "me",
        senderName: null,
        text: input.text ?? null,
        attachmentUrl: input.attachmentUrl ?? null,
        attachmentType: input.attachmentType ?? null,
        sentByUserId: null,
        sentAt: new Date().toISOString(),
        platformSentAt: null,
        deliveryStatus: "PENDING",
      };
      qc.setQueryData(
        ["inbox-conversation", input.brandId, input.conversationId],
        (prev: any) => {
          if (!prev) return prev;
          return {
            ...prev,
            messages: [...(prev.messages ?? []), optimistic],
          };
        },
      );
      return { optimisticId };
    },
    onSuccess: (serverRow, input, ctx) => {
      // Replace the optimistic row with the server response.
      qc.setQueryData(
        ["inbox-conversation", input.brandId, input.conversationId],
        (prev: any) => {
          if (!prev) return prev;
          const messages = (prev.messages ?? []).map((m: InboxMessage) =>
            m.id === ctx?.optimisticId ? serverRow : m,
          );
          return { ...prev, messages };
        },
      );
    },
    onError: (_err, input, ctx) => {
      // Mark the optimistic row as FAILED locally so UI shows retry.
      qc.setQueryData(
        ["inbox-conversation", input.brandId, input.conversationId],
        (prev: any) => {
          if (!prev) return prev;
          const messages = (prev.messages ?? []).map((m: InboxMessage) =>
            m.id === ctx?.optimisticId
              ? { ...m, deliveryStatus: "FAILED" as const }
              : m,
          );
          return { ...prev, messages };
        },
      );
    },
  });
}
